import React, { useState } from 'react';
import { Title } from '../../../account/account-settings.styled';
import { VSpace } from '../../../account/account-settings';
import {
  PlanHeaderSpan,
  PlanOptionCard,
  TrialDescription,
  CurrentPlanSpan,
  BenefitsRow,
  BenefitsIcon,
  BenefitsSpan,
  SelectedIconInner,
  SelectIcon,
  BillingOptionCard,
  BillingCycleSpan,
  BillingCycleDetails,
  BillingCycleTitleSpan,
  BillingCycleDescriptionSpan,
  BillingCycleHighlightSpan,
  BillingCycleStartsSpan,
} from './subscribe-now.styled';
import { PLANS } from '../../billing.constants';

export type SubscribeNowModalProps = {
  open: boolean;
  onClose: () => void;
  onNavigateToEditDetails: (billingCycle: 'annual' | 'monthly') => void;
  planName: string;
  trialEnd: string;
};

const SubscribeNowModal: React.FC<SubscribeNowModalProps> = (props) => {
  const {
    // open,
    onClose,
    onNavigateToEditDetails,
    planName,
    trialEnd,
  } = props;

  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'annual' | 'monthly'>('annual');

  const planDetails = {
    plans: {
      sanctum: {
        name: PLANS.sanctum.displayName,
        benefits: [
          'Steepen the exponential trajectory that opens your next funding or exit window',
          "Surface the asymmetric bets and hidden levers competitors can't see",
          'Built for how visionary founders actually operate: fast, intuitive, conviction-driven',
        ],
        billingCycles: {
          annual: {
            pricePerMonth: PLANS.sanctum.pricing.annualMonthly,
            totalPrice: PLANS.sanctum.pricing.annualTotal,
            savings: PLANS.sanctum.pricing.annualSavings,
          },
          monthly: {
            pricePerMonth: PLANS.sanctum.pricing.monthly,
          },
        },
      },
      northStar: {
        name: PLANS.northStar.displayName,
        benefits: [
          'Non-obvious insights that surface the blind spots, leverage points, and strategic bets your competitors miss',
          'Decision architecture calibrated to enterprise value, EBITDA expansion, and multiples that reflect category leadership',
          "Tightly controlled access for CEOs who've built something real and refuse conventional thinking",
        ],
        billingCycles: {
          annual: {
            pricePerMonth: PLANS.northStar.pricing.annualMonthly,
            totalPrice: PLANS.northStar.pricing.annualTotal,
            savings: PLANS.northStar.pricing.annualSavings,
          },
          monthly: {
            pricePerMonth: PLANS.northStar.pricing.monthly,
          },
        },
      },
    },
  };

  const getCurrentPlan = () => {
    if (!planName) return planDetails.plans.northStar;

    const planNameLower = planName.toLowerCase();
    if (planNameLower === 'sanctum') return planDetails.plans.sanctum;
    if (planNameLower === 'north_star' || planNameLower === 'north star') return planDetails.plans.northStar;

    return planDetails.plans.northStar;
  };

  const planDetailsSelected = getCurrentPlan();
  const trialEndDate = new Date(trialEnd);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxSizing: 'border-box',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Header with only logo */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
          position: 'sticky',
          top: 0,
          background: '#1A1A1C',
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <img
          onClick={onClose}
          title="Back"
          data-testid="settings-back"
          src="/settings-back.svg"
          alt="Back"
          width={44}
          height={44}
          style={{ display: 'block', cursor: 'pointer' }}
        />
        <div style={{ width: "100%" , display: 'flex', justifyContent: 'center' }}>
        <img src="/main-logo-white.svg" alt="logo" width={124} height="auto" draggable={false} />
        </div>
        <div style={{ width: "44px" }}></div>
      </header>

      {/* Content */}
      <div>
        <div
          style={{
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <>
            <Title>Subscribe now</Title>
            <VSpace $h={8} />
            <TrialDescription>
              Your {planDetailsSelected.name} trial ends on{' '}
              {trialEndDate.toISOString().split('T')[0]}. Choose your billing schedule to continue
              your private work with Mailander. You won&apos;t be charged until the trial ends.
            </TrialDescription>
          </>
          <PlanHeaderSpan>Your plan</PlanHeaderSpan>
          <PlanOptionCard>
            <CurrentPlanSpan>{planDetailsSelected.name}</CurrentPlanSpan>
            {planDetailsSelected.benefits.map((benefit, index) => (
              <BenefitsRow key={index}>
                <BenefitsIcon src="/checkmark.svg" />
                <BenefitsSpan>{benefit}</BenefitsSpan>
              </BenefitsRow>
            ))}
          </PlanOptionCard>
          <VSpace $h={16} />
          <BillingCycleSpan>Billing cycle</BillingCycleSpan>
          <BillingOptionCard
            selected={selectedBillingCycle === 'annual'}
            onClick={() => setSelectedBillingCycle('annual')}
          >
            <SelectIcon>{selectedBillingCycle === 'annual' && <SelectedIconInner />}</SelectIcon>
            <BillingCycleDetails>
              <BillingCycleTitleSpan>Annual Subscription</BillingCycleTitleSpan>
              <BillingCycleDescriptionSpan>
                ${planDetailsSelected.billingCycles.annual.pricePerMonth.toLocaleString()} per month
                (after discount)
              </BillingCycleDescriptionSpan>
              <BillingCycleDescriptionSpan>
                ${planDetailsSelected.billingCycles.annual.totalPrice.toLocaleString()} billed at
                outset of 12-month subscription
              </BillingCycleDescriptionSpan>
              <BillingCycleHighlightSpan>
                Save ${planDetailsSelected.billingCycles.annual.savings.toLocaleString()} per annum
              </BillingCycleHighlightSpan>
            </BillingCycleDetails>
          </BillingOptionCard>
          <BillingOptionCard
            selected={selectedBillingCycle === 'monthly'}
            onClick={() => setSelectedBillingCycle('monthly')}
          >
            <SelectIcon>{selectedBillingCycle === 'monthly' && <SelectedIconInner />}</SelectIcon>
            <BillingCycleDetails>
              <BillingCycleTitleSpan>Monthly Subscription</BillingCycleTitleSpan>
              <BillingCycleDescriptionSpan>
                ${planDetailsSelected.billingCycles.monthly.pricePerMonth.toLocaleString()} billed
                each month
              </BillingCycleDescriptionSpan>
              <BillingCycleDescriptionSpan>
                Pay as you go - cancel anytime
              </BillingCycleDescriptionSpan>
            </BillingCycleDetails>
          </BillingOptionCard>
          <BillingCycleStartsSpan>
            Billing starts on {trialEndDate.toLocaleDateString()} at the rate you choose.
          </BillingCycleStartsSpan>
          <VSpace $h={32} />
        </div>

        {/* Bottom buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            margin: '0 20px',
          }}
        >
          <button
            style={{
              display: 'flex',
              height: '44px',
              width: '100%',
              background: '#5E84ED',
              borderRadius: '12px',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              fontSize: '15px',
              fontFamily: 'Inter',
              fontWeight: 600,
              lineHeight: '21px',
              cursor: 'pointer',
              outline: 'none',
              border: 'none',
            }}
            onClick={() => onNavigateToEditDetails(selectedBillingCycle)}
          >
            Review billing details
          </button>
          <div
            style={{
              height: '44px',
              width: '100%',
              background: 'transparent',
              borderRadius: '12px',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <button
              style={{
                color: '#B7BDCB',
                background: 'transparent',
                border: 'none',
                fontSize: '15px',
                fontFamily: 'Inter',
                fontWeight: 600,
                lineHeight: '21px',
              }}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscribeNowModal;
